import { differenceWith, isEqual } from "lodash";
import moment from "moment";
import swal from 'sweetalert';
import { DEFAULT_PLANET_FIELDS, PLANETS, PLANET_COLORS, DEFAULT_ENABLED_PLANETS } from "./constants";
import toast from "react-hot-toast";

export const ucFirst = (string) => {
  if (!string) return string; // handle empty or null string
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const generateRandomId = (length = 7) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function confirmPopup(confirmMsg, ) {
  return new Promise((resolve) => {
    swal({
      title: "Are you sure?",
      text: confirmMsg,
      icon: "warning",
      buttons: true,
      dangerMode: false,
    }).then((isConfirmed) => {
      resolve(isConfirmed);
    });
  });
};

export function startOfDayTimestamp(date) {
  return moment(date).unix();
}

export function timeStampRange(start, end, max, min, unix = false) {
  // Set max and min to end and start respectively if they are not provided
  if (!max) {
    max = end;
  }
  if (!min) {
    min = start;
  }

  // Convert timestamps to Moment.js objects in UTC
  let startDate = moment.unix(start).utc();
  let endDate = moment.unix(end).utc();
  const maxDate = moment.unix(max).utc();
  const minDate = moment.unix(min).utc();

  // Adjust startDate and endDate based on min and max limits
  if (startDate.isBefore(minDate, "day")) {
    startDate = minDate.clone();
  }
  if (endDate.isAfter(maxDate, "day")) {
    endDate = maxDate.clone();
  }

  // Initialize an array to hold the range of dates
  let dateRange = [];

  // Loop from startDate to endDate, incrementing by 1 day each iteration
  let currentDate = startDate.clone();
  while (currentDate.isSameOrBefore(endDate, "day")) {
    if(unix) {
      dateRange.push(currentDate.clone().startOf("day").unix());  
    } else {
      dateRange.push(currentDate.clone().startOf("day").toDate());
    }
    currentDate.add(1, 'days');
  }

  return dateRange;
}

// Function to merge and remove duplicates timestamps
export function mergeTimestamps(arr1, arr2) {
  // Use Set to merge and remove duplicates
  const mergedSet = new Set([...arr1, ...arr2]);

  // Convert Set back to array
  return Array.from(mergedSet);
}

// Function to check if from and to timestamps are within the range
export function isTimestampWithinRange(timestamps, from, to) {
  // Find the minimum and maximum timestamps in the array
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);

  // Check if the from and to timestamps are within the range
  return from >= minTimestamp && to <= maxTimestamp;
}

// get the timestamps that are not within the provided timestamps array
export function getTimestampsDifference(firstArray, secondArray) {
  // Use filter to return timestamps that are not present in the first array
  return secondArray.filter(timestamp => !firstArray.includes(timestamp));
}


// get unique timestamps out of 2 arrays
export function findUniqueTimestamps(arr1, arr2) {
  // Convert arr1 timestamps to dates and store in a Set
  const set1 = new Set(arr1.map(ts => moment.unix(ts).format('YYYY-MM-DD')));

  // Filter arr2 timestamps if their corresponding date is not in set1
  return arr2.filter(ts => {
    const date = moment.unix(ts).format('YYYY-MM-DD');
    return !set1.has(date);
  });
}

// get the timestamps that are after the selected timestamp
export function getTimestampsAfter(timestamp, timestampsArr) {
  let filteredArray = timestampsArr.filter(item => moment.unix(item).isSameOrAfter(moment.unix(timestamp)));
  return filteredArray.map(timestamp => moment.unix(timestamp).toDate());
}

export function createNewPlanet(data) {
  let planetName = data.name.toLowerCase();

  // allow adding known planets only
  if(!PLANETS[planetName]) {
    return null;
  }

  return {
    ...DEFAULT_PLANET_FIELDS,
    ...data,
    name: planetName,
    aspect: data.aspect || "conjunction",
    orb: data.orb,
    key: `${data.name}_${generateRandomId()}`,
    lineColor: PLANET_COLORS[planetName] || PLANET_COLORS["sun"],
    enabled: data.enabled || DEFAULT_ENABLED_PLANETS.includes(planetName) || false,
  }
}

// Function to calculate the difference in days between two Unix timestamps using Moment.js
export function timestampDifferenceInDays(timestamp1, timestamp2) {
  // Convert Unix timestamps to Moment objects
  const date1 = moment.unix(timestamp1);
  const date2 = moment.unix(timestamp2);

  // Calculate the difference in days
  const differenceInDays = date2.diff(date1, 'days');
  
  return differenceInDays;
}

function getRandomValue(min, max, modifier = 0) {
  return Math.floor(Math.random() * (max - min + 1)) + min + modifier;
}

export function generateTimestamps(startDate, endDate, modifier) {
  const timestamps = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Iterate from start date to end date
  for (let date = start; date <= end; date.setDate(date.getDate() + 1)) {
    let time = new Date(date).getTime() / 1000;
    let degree = getRandomValue(10, 200, modifier);
    timestamps.push({
      time,
      price: degree,
    });
  }

  return timestamps;
}

export function sortTimestamps(timestamps) {
  return timestamps.sort((a, b) => a - b);
}

export function logTimestamp(timestamp) {
  return moment.unix(timestamp).format("DD/MM/YYYY");
}

export function getLanguageFromURL() {
  const regex = new RegExp("[\\?&]lang=([^&#]*)");
  const results = regex.exec(window.location.search);
  return results === null
    ? null
    : decodeURIComponent(results[1].replace(/\+/g, " "));
}

/**
 * Function to check if a timestamp is in milliseconds
 */
export function isTimestampMilliseconds(timestamp) {
  // Check if the timestamp is within the range of valid millisecond timestamps
  if (timestamp > 1e12 || timestamp < -1e12) {
    return true;
  } else {
    return false;
  }
}

export const successMessageToast = (msg) => {
  toast.success(msg);
}

export const errorMessageToast = (msg, options = {}) => {
  toast.error(msg, options);
}

export const calculateMaxDates = (startDate, endDate) => {
  // Ensure both startDate and endDate exist
  if (!startDate || !endDate) 
    return [
      null,
      null
    ];

  // Use moment to parse dates and get the difference in days
  const now = moment();
  const start = moment(startDate);
  const end = moment(endDate);

  // Calculate the number of days between start and end date
  const startEndDiff = Math.max(0, end.diff(start, 'days')); 

  // Calculate the number of days past and future
  const maxDatePast = moment().subtract(startEndDiff, "days").toDate();
  const maxDateFuture = end.toDate();

  return [maxDatePast, maxDateFuture];
};

export const calculatePastDate = (date, maxPastDate) => {
  const currentDate = moment(date);
  const pastDateLimit = moment(maxPastDate);

  // If maxPastDate is in the past relative to the provided date, return maxPastDate; otherwise return the date itself
  return pastDateLimit.isBefore(currentDate) ? currentDate : pastDateLimit;
};

export const calculateFutureDate = (date, maxFutureDate) => {
  const currentDate = moment(date);
  const futureDateLimit = moment(maxFutureDate);

  // If maxFutureDate is in the future relative to the provided date, return maxFutureDate; otherwise return the date itself
  return futureDateLimit.isAfter(currentDate) ? futureDateLimit : currentDate;
};

export const trimTextWithEllipses = (text, limit = 20, ellipses = "...") => {
  if (!text || typeof text !== "string") return "";
  if (text.length <= limit) return text;
  return text.slice(0, limit).trim() + ellipses;
};

export const formatDate = (date, format = "DD-MMM-YYYY hh:mm A") => {
  return moment(date).format(format);
}

export const mergeDegrees = (array1, array2) => {
  // Create a map to merge the arrays by price
  const merged = new Map();

  // Add elements from array1
  array1.forEach(item => merged.set(item.price, item));

  // Add elements from array2 (this will replace items with the same price)
  array2.forEach(item => merged.set(item.price, item));

  // Convert the map back to an array
  const result = Array.from(merged.values());

  return result;
}

export const formatString = (str) => {
  if (!str) return '';
  return str
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

export const tvChartGetStyleByNumber = (number) => {
  switch(number) {
    case 0:
      return "solid";

    case 1:
      return "dotted";
    
    case 2:
      return "dashed";

    default:
      return "solid";
  }
}